import React from 'react';
import Drawer, { FooterSpacer } from '../Drawer';
import Button from '../Button';
import Field from '../Field';
import MessageAttachments from './MessageAttachments';
import MessageBodyEditor from './MessageBodyEditor';
import MessageTemplatesMenu from './MessageTemplatesMenu';
import { useMessageTemplatesIfProvided } from '../../data/useMessageTemplates';
import { composeLimits, composeWords } from '../../domain/notifications';
import s from './drawers.module.css';

// The composer behind Message History's New Message ▾ (#522) and a row's
// Forward (#546), as MMS's (#547, #599, #600): From, To as removable chips with
// a `Select...` menu that adds the people New Message's entries name or a
// studio-wide group (`groups`, with "Show all" widening them), Extra
// Recipients for addresses not on the profile, Subject, the message, CC me,
// and Send Email — Immediately or at a future date and time. The state and
// the send are `useNewMessage` (src/hooks/useNewMessage.js).
//
// The message (#596, #597, #598) is Markdown in a plain box, as a lesson note
// is, with MMS's editor reduced to what Markdown can say: a toolbar whose
// buttons write the Markdown around the selection, MMS's placeholder menu
// (#604, `%FirstName%` and the rest, filled per recipient at the send),
// Insert Image from the studio's library, Preview — the email's own renderer,
// each placeholder shown as `<< its words >>` — and `Templates ▾` with Save New
// Template. Attach Files under it is MMS's (#605): a file uploaded at once and
// sent as a real attachment (MessageAttachments.jsx). core/src/compose.rs says
// what of MMS's each keeps. The editor is MessageBodyEditor.jsx, shared with a
// Custom Email Template's drawer (#608), with the Templates menu added here.
// A composer mounted without the library, the templates or an upload (a test)
// offers none of those controls rather than a broken one.

export default function NewMessageDrawer({ composer, choices = [], groups = [] }) {
  const words = composeWords();
  const limits = composeLimits();
  const templates = useMessageTemplatesIfProvided();
  const entries = [...choices, ...groups];

  return (
    <Drawer
      open={composer.isOpen}
      onClose={composer.close}
      title={composer.title || words.newMessage}
      width={560}
      footer={(
        <>
          <Button variant="secondary" onClick={composer.close}>{words.cancel}</Button>
          <FooterSpacer />
          <Button loading={composer.sending} onClick={composer.submit}>{words.send}</Button>
        </>
      )}
    >
      {composer.fromChoices?.length ? (
        <Field label={words.from}>
          <select
            className={s.input}
            aria-label={words.from}
            value={composer.from || ''}
            onChange={(e) => composer.change({ from: e.target.value })}
          >
            {composer.fromChoices.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
          </select>
        </Field>
      ) : null}
      <div className={s.section}>
        <Field label={words.to} hint={words.bcc}>
          <ul className={s.recipients} aria-label={words.to}>
            {composer.to.map((r) => (
              <li key={r.address} className={s.chip}>
                <span>{r.name ? `${r.name} <${r.address}>` : r.address}</span>
                <button
                  type="button"
                  className={s.chipRemove}
                  aria-label={`Remove ${r.address}`}
                  onClick={() => composer.remove(r.address)}
                >
                  ×
                </button>
              </li>
            ))}
          </ul>
        </Field>
      </div>
      {entries.length ? (
        <select
          className={`${s.input} ${s.chipAdd}`}
          aria-label={words.forwardSelect}
          value=""
          onChange={(e) => {
            const choice = entries.find((ch) => ch.key === e.target.value);
            if (choice) composer.add(choice);
          }}
        >
          <option value="">{words.forwardSelect}</option>
          {entries.map((ch) => <option key={ch.key} value={ch.key}>{ch.label}</option>)}
        </select>
      ) : null}
      <label className={`${s.section} ${s.check}`}>
        <input
          type="checkbox"
          checked={composer.showAll}
          onChange={(e) => composer.change({ showAll: e.target.checked })}
        />
        {words.showAll}
      </label>
      <div className={s.section}>
        <Field label={words.extra} hint={words.extraHint}>
          <input
            className={s.input}
            value={composer.extra}
            onChange={(e) => composer.change({ extra: e.target.value })}
            // MMS's "press the Enter key": the next address starts after a
            // semicolon, rather than the form going anywhere.
            onKeyDown={(e) => {
              if (e.key !== 'Enter') return;
              e.preventDefault();
              const typed = composer.extra.trim();
              if (typed && !typed.endsWith(';')) composer.change({ extra: `${typed}; ` });
            }}
          />
        </Field>
      </div>
      <div className={s.section}>
        <Field label={words.subject} required>
          <input className={s.input} value={composer.subject} maxLength={limits.subject}
                 onChange={(e) => composer.change({ subject: e.target.value })} />
        </Field>
      </div>
      <div className={s.section}>
        <MessageBodyEditor
          label={words.body}
          required
          value={composer.body}
          onChange={(body) => composer.change({ body })}
          edits={composer}
          error={composer.error}
          notice={composer.notice}
          tools={templates ? <MessageTemplatesMenu templates={templates} composer={composer} /> : null}
        />
      </div>
      {composer.canAttach ? <MessageAttachments composer={composer} /> : null}
      {composer.canCcMe ? (
        <label className={`${s.section} ${s.check}`}>
          <input
            type="checkbox"
            checked={composer.ccMe}
            onChange={(e) => composer.change({ ccMe: e.target.checked })}
          />
          {words.ccMe}
        </label>
      ) : null}
      <fieldset className={s.fieldset}>
        <legend className={s.legend}>{words.sendEmail}</legend>
        <label className={s.choice}>
          <input type="radio" name="sendEmail" checked={!composer.later} onChange={() => composer.change({ later: false })} />
          <span>{words.immediately}<span className={s.choiceHint}>{words.immediatelyHint}</span></span>
        </label>
        <label className={s.choice}>
          <input type="radio" name="sendEmail" checked={composer.later} onChange={() => composer.change({ later: true })} />
          <span>{words.later}<span className={s.choiceHint}>{words.laterHint}</span></span>
        </label>
        {composer.later ? (
          <div className={s.when}>
            <Field label={words.date}>
              <input type="date" className={s.input} value={composer.date}
                     onChange={(e) => composer.change({ date: e.target.value })} />
            </Field>
            <Field label={words.time}>
              {/* MMS's picker steps by five minutes. */}
              <input type="time" step={300} className={s.input} value={composer.time}
                     onChange={(e) => composer.change({ time: e.target.value })} />
            </Field>
          </div>
        ) : null}
      </fieldset>
    </Drawer>
  );
}
